import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import Global from '../../../../../store/global-selectors';
import {
  getTerminalPackingRecommendationStatus,
  PackingRecommendationStatus,
} from '../../../../../packing/services/packing-recommendation-status';
import type { JobResultResponse, PackingError } from '../../../../../packing/services/packing-service';
import type { AssignedItem, Package } from '../../../../../components/modals/multi-package-modal-dnd/types';
import type { BoxResult } from './types';

type AppliedPackageSnapshot = {
  packageId?: string;
  weight?: number;
  dimensions?: Record<string, number>;
};

type AppliedStateEntry = {
  isApplied: boolean;
  packageName: string;
  isMultiBox: boolean;
  boxes: BoxResult[];
  appliedPackages: AppliedPackageSnapshot[] | null;
  appliedAssignments?: AssignedItem[][];
  preApplyPackages?: Package[];
  hydratedFromBackend?: boolean;
  appliedItemsSignature?: string;
};

type UsePackingAppliedReturn = {
  isPackingApplied: boolean;
  isCalculating: boolean;
  packageName: string | null;
};

export type PackingAppliedDetail = {
  fulfillmentPlanId: string;
  packageName: string;
  isMultiBox: boolean;
  boxes: BoxResult[];
  appliedPackages?: AppliedPackageSnapshot[] | null;
  appliedAssignments?: AssignedItem[][];
  preApplyPackages?: Package[];
  hydratedFromBackend?: boolean;
  appliedItemsSignature?: string;
};

type ActiveJob = {
  jobId: string;
  startTime: number;
};

type EventBus<Args extends unknown[]> = {
  listeners: Set<(fpId: string, ...args: Args) => void>;
  notify: (fpId: string, ...args: Args) => void;
  subscribe: (listener: (fpId: string, ...args: Args) => void) => () => void;
};

const createEventBus = <Args extends unknown[]>(): EventBus<Args> => {
  const listeners = new Set<(fpId: string, ...args: Args) => void>();

  const notify = (fpId: string, ...args: Args): void => {
    listeners.forEach((listener) => listener(fpId, ...args));
  };

  const subscribe = (listener: (fpId: string, ...args: Args) => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  return { listeners, notify, subscribe };
};

const useEventBusListener = <Args extends unknown[]>(
  bus: EventBus<Args>,
  fulfillmentPlanId: string,
  callback: (...args: Args) => void,
): void => {
  const handleEvent = useCallback(
    (fpId: string, ...args: Args) => {
      if (fpId === fulfillmentPlanId) callback(...args);
    },
    [fulfillmentPlanId, callback],
  );

  useEffect(() => bus.subscribe(handleEvent), [bus, handleEvent]);
};

const appliedBus = createEventBus<[packageName: string | null]>();
const calculateBus = createEventBus<[isRerun: boolean]>();
const calculatingBus = createEventBus<[isCalculating: boolean]>();
const resultClearedBus = createEventBus<[]>();
const resultReadyBus = createEventBus<[]>();

const MAX_APPLIED_ENTRIES = 100;
const appliedState = new Map<string, AppliedStateEntry>();
const dismissedPersistedState = new Set<string>();
const calculatingState = new Set<string>();
const errorState = new Map<string, PackingError>();
const activeJobs = new Map<string, ActiveJob>();
const resultCache = new Map<string, JobResultResponse>();
const multiPackageDrawerOpenCounts = new Map<string, number>();

export const getAppliedState = (fulfillmentPlanId: string): AppliedStateEntry | undefined => appliedState.get(fulfillmentPlanId);

export const markPersistedAppliedDismissed = (fulfillmentPlanId: string): void => {
  dismissedPersistedState.add(fulfillmentPlanId);
  appliedBus.notify(fulfillmentPlanId, null);
};

export const isPersistedAppliedDismissed = (fulfillmentPlanId: string): boolean => dismissedPersistedState.has(fulfillmentPlanId);

export const onAppliedChange = (listener: (fpId: string, packageName: string | null) => void): (() => void) =>
  appliedBus.subscribe(listener);

export const isCurrentlyCalculating = (fulfillmentPlanId: string): boolean => calculatingState.has(fulfillmentPlanId);

export const setMultiPackageDrawerOpen = (fulfillmentPlanId: string, isOpen: boolean): void => {
  const currentCount = multiPackageDrawerOpenCounts.get(fulfillmentPlanId) ?? 0;
  if (isOpen) {
    multiPackageDrawerOpenCounts.set(fulfillmentPlanId, currentCount + 1);
  } else if (currentCount <= 1) {
    multiPackageDrawerOpenCounts.delete(fulfillmentPlanId);
  } else {
    multiPackageDrawerOpenCounts.set(fulfillmentPlanId, currentCount - 1);
  }
};

export const isMultiPackageDrawerOpen = (fulfillmentPlanId: string): boolean =>
  (multiPackageDrawerOpenCounts.get(fulfillmentPlanId) ?? 0) > 0;

export const setActiveJob = (fulfillmentPlanId: string, jobId: string, startTime: number): void => {
  if (activeJobs.size >= MAX_APPLIED_ENTRIES) {
    const oldestKey = activeJobs.keys().next().value;
    if (oldestKey) activeJobs.delete(oldestKey);
  }
  activeJobs.set(fulfillmentPlanId, { jobId, startTime });
};

export const getActiveJob = (fulfillmentPlanId: string): ActiveJob | null => activeJobs.get(fulfillmentPlanId) ?? null;

export const markError = (fulfillmentPlanId: string, error: PackingError): void => {
  if (errorState.size >= MAX_APPLIED_ENTRIES) {
    const oldest = errorState.keys().next().value;
    if (oldest) errorState.delete(oldest);
  }
  errorState.set(fulfillmentPlanId, error);
};

export const hasError = (fulfillmentPlanId: string): boolean => errorState.has(fulfillmentPlanId);

export const getPersistedError = (fulfillmentPlanId: string): PackingError | null => errorState.get(fulfillmentPlanId) ?? null;

export const clearError = (fulfillmentPlanId: string): void => {
  errorState.delete(fulfillmentPlanId);
};

export const cacheResult = (fulfillmentPlanId: string, result: JobResultResponse): void => {
  if (resultCache.size >= MAX_APPLIED_ENTRIES) {
    const oldestKey = resultCache.keys().next().value;
    if (oldestKey) resultCache.delete(oldestKey);
  }
  resultCache.set(fulfillmentPlanId, result);
  resultReadyBus.notify(fulfillmentPlanId);
};

export const getCachedResult = (fulfillmentPlanId: string): JobResultResponse | null => resultCache.get(fulfillmentPlanId) ?? null;

export const clearCachedResult = (fulfillmentPlanId: string): void => {
  resultCache.delete(fulfillmentPlanId);
  resultClearedBus.notify(fulfillmentPlanId);
};

export const markPackingApplied = ({
  fulfillmentPlanId,
  packageName,
  isMultiBox,
  boxes,
  appliedPackages = null,
  appliedAssignments,
  preApplyPackages,
  hydratedFromBackend,
  appliedItemsSignature,
}: PackingAppliedDetail): void => {
  if (appliedState.size >= MAX_APPLIED_ENTRIES) {
    const oldestKey = appliedState.keys().next().value;
    if (oldestKey) appliedState.delete(oldestKey);
  }

  const preservedPreApply = preApplyPackages ?? appliedState.get(fulfillmentPlanId)?.preApplyPackages;
  dismissedPersistedState.delete(fulfillmentPlanId);
  appliedState.set(fulfillmentPlanId, {
    isApplied: true,
    packageName,
    isMultiBox,
    boxes,
    appliedPackages,
    appliedAssignments,
    preApplyPackages: preservedPreApply,
    hydratedFromBackend: hydratedFromBackend ?? appliedState.get(fulfillmentPlanId)?.hydratedFromBackend,
    appliedItemsSignature: appliedItemsSignature ?? appliedState.get(fulfillmentPlanId)?.appliedItemsSignature,
  });
  calculatingState.delete(fulfillmentPlanId);
  activeJobs.delete(fulfillmentPlanId);
  resultCache.delete(fulfillmentPlanId);
  appliedBus.notify(fulfillmentPlanId, packageName);
  calculatingBus.notify(fulfillmentPlanId, false);
};

export const resetPackingApplied = (fulfillmentPlanId: string): void => {
  appliedState.delete(fulfillmentPlanId);
  calculatingState.delete(fulfillmentPlanId);
  activeJobs.delete(fulfillmentPlanId);
  resultCache.delete(fulfillmentPlanId);
  errorState.delete(fulfillmentPlanId);
  appliedBus.notify(fulfillmentPlanId, null);
  calculatingBus.notify(fulfillmentPlanId, false);
};

export const markCalculating = (fulfillmentPlanId: string): void => {
  calculatingState.add(fulfillmentPlanId);
  calculatingBus.notify(fulfillmentPlanId, true);
};

export const clearCalculating = (fulfillmentPlanId: string): void => {
  calculatingState.delete(fulfillmentPlanId);
  activeJobs.delete(fulfillmentPlanId);
  errorState.delete(fulfillmentPlanId);
  calculatingBus.notify(fulfillmentPlanId, false);
};

export const triggerCalculation = (fulfillmentPlanId: string, isRerun = false): void => {
  if (calculatingState.has(fulfillmentPlanId)) return;
  dismissedPersistedState.delete(fulfillmentPlanId);
  if (isRerun) {
    resetPackingApplied(fulfillmentPlanId);
  }
  markCalculating(fulfillmentPlanId);
  calculateBus.notify(fulfillmentPlanId, isRerun);
  if (calculateBus.listeners.size === 0) {
    clearCalculating(fulfillmentPlanId);
  }
};

export const useOnTriggerCalculation = (fulfillmentPlanId: string, callback: (isRerun: boolean) => void): void => {
  useEventBusListener(calculateBus, fulfillmentPlanId, callback);
};

export const useOnResultCleared = (fulfillmentPlanId: string, callback: () => void): void => {
  useEventBusListener(resultClearedBus, fulfillmentPlanId, callback);
};

export const useOnResultReady = (fulfillmentPlanId: string, callback: () => void): void => {
  useEventBusListener(resultReadyBus, fulfillmentPlanId, callback);
};

export const usePackingApplied = (fulfillmentPlanId: string): UsePackingAppliedReturn => {
  const persistedState = appliedState.get(fulfillmentPlanId);
  const [isPackingApplied, setIsPackingApplied] = useState(persistedState?.isApplied ?? false);
  const [packageName, setPackageName] = useState<string | null>(persistedState?.packageName ?? null);
  const [isCalculating, setIsCalculating] = useState(calculatingState.has(fulfillmentPlanId));
  const [isPersistedDismissed, setIsPersistedDismissed] = useState(() => dismissedPersistedState.has(fulfillmentPlanId));

  const isStatusApplied = useSelector(
    (state) =>
      getTerminalPackingRecommendationStatus(Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) ===
      PackingRecommendationStatus.Applied,
  );

  const handleChange = useCallback(
    (name: string | null) => {
      setIsPackingApplied(name !== null);
      setPackageName(name);
      setIsPersistedDismissed(dismissedPersistedState.has(fulfillmentPlanId));
    },
    [fulfillmentPlanId],
  );

  useEventBusListener(appliedBus, fulfillmentPlanId, handleChange);
  useEventBusListener(calculatingBus, fulfillmentPlanId, setIsCalculating);

  return { isPackingApplied: isPackingApplied || (isStatusApplied && !isPersistedDismissed), isCalculating, packageName };
};
